import React, { useState, useEffect, useRef } from 'react';
import { Coffee, Pizza, UtensilsCrossed, ShieldOff, StopCircle, Clock as ClockIcon } from 'lucide-react';
import { safeFetch, extractError } from '../../lib/safeFetch';
import { getPreciseLocation } from '../../lib/preciseLocation';
import { speak, primeTts } from '../../lib/voicePrompt';
import GateOtpModal from './GateOtpModal';
import LocationVerifyingModal from './LocationVerifyingModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// User-facing wording for the overrun reminder — same line is shown in the
// red banner AND spoken aloud by the TTS engine. Keep them in sync so the
// staff hears exactly what they see.
const OVERRUN_MESSAGE =
  'Hi, please note the break is overrun. Please get back to office to end the break. Do not end the break outside office.';

// ---- Audio: 2-minute "break ending" beep --------------------------------
// Uses the Web Audio API so we don't have to ship an MP3 with the APK.
// Plays a short tri-tone (G5-A5-B5) that's loud enough to catch attention
// over road noise but short enough not to annoy.
const playWarningBeep = () => {
  try {
    // Safari/older Chrome may not have AudioContext on `window`.
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const tones = [784, 880, 988]; // G5, A5, B5
    tones.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, ctx.currentTime + i * 0.22);
      gain.gain.exponentialRampToValueAtTime(0.35, ctx.currentTime + i * 0.22 + 0.05);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + i * 0.22 + 0.18);
      osc.connect(gain); gain.connect(ctx.destination);
      osc.start(ctx.currentTime + i * 0.22);
      osc.stop(ctx.currentTime + i * 0.22 + 0.2);
    });
    // Also try a haptic buzz on Android — silent on iOS / desktop.
    if (typeof navigator !== 'undefined' && navigator.vibrate) {
      try { navigator.vibrate([120, 80, 120]); } catch {}
    }
  } catch {}
};

// ---- Audio: LOUD overrun siren ------------------------------------------
// Played in a 6-tone descending alarm pattern at near-max gain. Designed to
// cut through phone speakers + road noise so staff notice even while
// using their phone for something else. Coupled with a long buzz pattern
// so the device vibrates urgently.
const playOverrunAlarm = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    // Two passes through a five-note urgent pattern.
    const pattern = [988, 784, 988, 784, 988, 784, 988, 784, 988, 784];
    pattern.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'square'; // square waves cut through better than sine
      osc.frequency.value = freq;
      const start = ctx.currentTime + i * 0.28;
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.85, start + 0.04);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.24);
      osc.connect(gain); gain.connect(ctx.destination);
      osc.start(start);
      osc.stop(start + 0.27);
    });
    if (typeof navigator !== 'undefined' && navigator.vibrate) {
      try { navigator.vibrate([300, 120, 300, 120, 300, 120, 300]); } catch {}
    }
  } catch {}
};

const QUICK_BREAK_TYPES = [
  { id: 'lunch', label: 'Lunch', icon: UtensilsCrossed, color: 'bg-orange-500 hover:bg-orange-600' },
  { id: 'tea', label: 'Tea Break', icon: Coffee, color: 'bg-amber-600 hover:bg-amber-700', hint: 'auto-slot' },
  { id: 'dinner', label: 'Dinner', icon: Pizza, color: 'bg-rose-600 hover:bg-rose-700' }
];

// Permission + legacy "tea" rows still appear in "Today's Breaks" report.
const BREAK_TYPES = [
  ...QUICK_BREAK_TYPES,
  { id: 'tea1', label: 'Tea (morning)', icon: Coffee, color: 'bg-amber-500' },
  { id: 'tea2', label: 'Tea (afternoon)', icon: Coffee, color: 'bg-amber-600' },
  { id: 'tea_evening', label: 'Tea (evening)', icon: Coffee, color: 'bg-amber-700' },
  { id: 'permission', label: 'Permission', icon: ShieldOff, color: 'bg-purple-600 hover:bg-purple-700' }
];

const formatDuration = (hrs) => {
  const totalMin = Math.round((hrs || 0) * 60);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h === 0) return `${m}m`;
  return `${h}h ${m}m`;
};

const labelOf = (type) => (BREAK_TYPES.find(t => t.id === type) || {}).label || type;

export default function BreaksTracker() {
  const [breaks, setBreaks] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [otpModal, setOtpModal] = useState(null);
  const [verifying, setVerifying] = useState(null); // { action, taken, lastAccuracy }
  const [checkedIn, setCheckedIn] = useState(true); // optimistic; we'll verify
  // Live "X minutes remaining" warning banner for the ONGOING break.
  // Updates every 15 s. Beep fires once per break (tracked in `warnedRef`).
  const [endWarning, setEndWarning] = useState(null); // { type, minsLeft } or null
  const warnedRef = useRef(new Set()); // break IDs we've already beeped for
  // Live "break has overrun" state — separate from `endWarning` because the
  // overrun alarm + UK-female TTS prompt repeats every 60 s until the staff
  // ends the break, whereas the pre-warning beep fires only once.
  const [overrun, setOverrun] = useState(null); // { type, overByMin } or null
  const overrunLastSpokeAt = useRef(0); // wall-clock of last TTS prompt

  useEffect(() => { fetchToday(); fetchAttendanceState(); }, []);

  const fetchAttendanceState = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/attendance/today`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (r.ok) {
        const d = await r.json();
        const open = d?.has_open_session ?? !!(d?.check_in && !d?.check_out);
        setCheckedIn(!!open);
      }
    } catch {}
  };

  const fetchToday = async () => {
    const token = localStorage.getItem('token');
    const today = new Date().toISOString().slice(0, 10);
    const [bRes, pRes] = await Promise.all([
      fetch(`${API_URL}/api/breaks/today`, { headers: { 'Authorization': `Bearer ${token}` } }),
      fetch(`${API_URL}/api/permissions/my`, { headers: { 'Authorization': `Bearer ${token}` } })
    ]);
    const bData = await bRes.json().catch(() => []);
    const pData = await pRes.json().catch(() => []);
    setBreaks(Array.isArray(bData) ? bData : []);
    // Only today's permissions, and only those that have been "punched out" so a duration exists
    setPermissions(
      (Array.isArray(pData) ? pData : []).filter(p => p.date === today && (p.status === 'ongoing' || p.status === 'completed' || p.status === 'pending'))
    );
  };

  const ongoing = breaks.find(b => b.status === 'ongoing');

  // ---- 2-minute "break ending soon" warning ------------------------------
  // Runs once every 15 s while a break is ongoing. When the remaining time
  // drops to ≤ 2 minutes (and is still positive), play a beep and surface a
  // banner so staff don't accidentally overstay. When the remaining time
  // becomes NEGATIVE the break has overrun — switch to the loud alarm +
  // UK-female TTS reminder that repeats every 60 s until they end it.
  // After the break ends, we forget all warning state so the next break
  // starts fresh.
  useEffect(() => {
    if (!ongoing || !ongoing.out?.time || !ongoing.allowed_duration_hours) {
      setEndWarning(null);
      setOverrun(null);
      overrunLastSpokeAt.current = 0;
      return;
    }
    const computeRemaining = () => {
      const outAt = new Date(ongoing.out.time).getTime();
      if (Number.isNaN(outAt)) return null;
      const endsAt = outAt + ongoing.allowed_duration_hours * 3600 * 1000;
      const minsLeft = Math.ceil((endsAt - Date.now()) / 60000);
      return minsLeft;
    };
    const check = () => {
      const minsLeft = computeRemaining();
      if (minsLeft == null) return;
      if (minsLeft <= 0) {
        // Overrun. Fire the loud alarm + speak the UK-female reminder
        // every 60 s (use wall-clock so we don't spam if the effect's
        // setInterval is throttled by the browser when tab is background).
        const overByMin = -minsLeft;
        setOverrun({ type: ongoing.type, overByMin });
        setEndWarning(null);
        const now = Date.now();
        if (now - overrunLastSpokeAt.current >= 60_000) {
          overrunLastSpokeAt.current = now;
          playOverrunAlarm();
          // Slight delay so the alarm tones don't collide with the TTS line.
          setTimeout(() => speak(OVERRUN_MESSAGE), 1500);
        }
        return;
      }
      if (minsLeft <= 2) {
        // Beep only once per break (covers both 2m & 1m ticks).
        if (!warnedRef.current.has(ongoing.id)) {
          warnedRef.current.add(ongoing.id);
          playWarningBeep();
        }
        setEndWarning({ type: ongoing.type, minsLeft });
        setOverrun(null);
      } else {
        setEndWarning(null);
        setOverrun(null);
      }
    };
    check();
    // Tick every 15 s pre-overrun, 30 s once overrun (TTS itself uses a 60 s
    // wall-clock gate, so we just need a steady heartbeat).
    const id = setInterval(check, 15_000);
    return () => clearInterval(id);
  }, [ongoing]);

  // Unified rows for the "Today's Breaks" list — quick breaks + formal permissions
  const todayRows = [
    ...breaks.map(b => ({
      kind: 'break',
      id: b.id,
      type: b.type,
      out_time: b.out?.time,
      in_time: b.in?.time,
      status: b.status,
      duration_hours: b.duration_hours,
      excess_duration_hours: b.excess_duration_hours
    })),
    ...permissions.filter(p => p.out?.time).map(p => ({
      kind: 'permission',
      id: `perm-${p.id}`,
      type: 'permission',
      out_time: p.out?.time,
      in_time: p.in?.time,
      status: p.status === 'ongoing' ? 'ongoing' : 'completed',
      duration_hours: p.actual_hours || 0,
      excess_duration_hours: Math.max(0, (p.actual_hours || 0) - (p.requested_hours || 0))
    }))
  ].sort((a, b) => new Date(b.out_time || 0) - new Date(a.out_time || 0));

  const startBreak = async (type) => {
    // Capture the moment the staff tapped the button BEFORE any async work
    // (GPS poll, network retries) — see Point #2 backend fix. We send this
    // as `client_timestamp` so the recorded `out.time` matches the actual
    // tap, not the post-GPS-and-retry server time.
    const tapTs = new Date().toISOString();
    // Prime the TTS engine on this fresh user gesture so the eventual
    // overrun reminder (which fires autonomously up to ~60 min later) can
    // bypass the browser's autoplay restrictions. Silent — no audio plays.
    primeTts();
    setErrorMsg('');
    if (!checkedIn) {
      setErrorMsg('Please Check In first (in the Visits tab) to take a break.');
      return;
    }
    if (ongoing) {
      setErrorMsg(`Already on ${labelOf(ongoing.type)}. End it first.`);
      return;
    }
    if (loading) return;
    setLoading(true);
    setVerifying({ action: `${labelOf(type)} — OUT`, taken: 0, lastAccuracy: null });
    let loc;
    try {
      loc = await getPreciseLocation({
        onProgress: (taken, _t, last) =>
          setVerifying((v) => v && { ...v, taken, lastAccuracy: last?.accuracy ?? v.lastAccuracy }),
      });
    } catch (e) {
      setErrorMsg(e?.message || 'Could not fetch your location.');
      setVerifying(null);
      setLoading(false);
      return;
    }
    setVerifying(null);
    const token = localStorage.getItem('token');
    const result = await safeFetch(`${API_URL}/api/breaks/start`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ type, ...loc, client_timestamp: tapTs })
    });
    if (result.ok) {
      const mov = result.data?.gate_movement;
      if (mov?.entry_otp) {
        setOtpModal({
          otp: mov.entry_otp,
          label: `${labelOf(type)} — OUT`,
          subtitle: 'Break started',
          autoApproved: mov.auto_approved,
        });
      }
      await fetchToday();
      try { window.dispatchEvent(new Event('carryover:refresh')); } catch {}
    } else {
      setErrorMsg(extractError(result, 'Failed to start break'));
    }
    setLoading(false);
  };

  const endBreak = async (breakId) => {
    // Capture button-press time before GPS poll + network retries
    // (see Point #2 fix in startBreak).
    const tapTs = new Date().toISOString();
    setErrorMsg('');
    if (loading) return;
    setLoading(true);
    setVerifying({ action: 'Break — IN', taken: 0, lastAccuracy: null });
    let loc;
    try {
      loc = await getPreciseLocation({
        onProgress: (taken, _t, last) =>
          setVerifying((v) => v && { ...v, taken, lastAccuracy: last?.accuracy ?? v.lastAccuracy }),
      });
    } catch (e) {
      setErrorMsg(e?.message || 'Could not fetch your location.');
      setVerifying(null);
      setLoading(false);
      return;
    }
    setVerifying(null);
    const token = localStorage.getItem('token');
    const result = await safeFetch(`${API_URL}/api/breaks/end`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ break_id: breakId, ...loc, client_timestamp: tapTs })
    });
    if (result.ok) {
      if (result.data?.exit_otp) {
        setOtpModal({
          otp: result.data.exit_otp,
          label: 'Break — IN',
          subtitle: 'Welcome back',
          autoApproved: false,
        });
      }
      setErrorMsg('');
      await fetchToday();
    } else {
      setErrorMsg(extractError(result, 'Failed to end break'));
    }
    setLoading(false);
  };

  return (
    <div className="bg-white rounded-xl p-5 shadow-sm border" data-testid="breaks-tracker">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          <Coffee className="text-orange-500" size={20} />
          <h3 className="font-semibold">Breaks</h3>
        </div>
        {ongoing && (
          <span className="text-xs px-2 py-1 rounded-full bg-orange-100 text-orange-700" data-testid="ongoing-break-pill">
            {labelOf(ongoing.type)} ongoing
          </span>
        )}
      </div>

      {!ongoing && (
        <>
          {!checkedIn && (
            <div className="mb-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800 flex items-start gap-2" data-testid="break-checked-out-banner">
              <span className="text-base leading-none">⚠️</span>
              <span className="flex-1">
                You're <strong>checked out</strong>. Go to the <strong>Visits</strong> tab and tap <strong>Check In</strong> before starting a break.
              </span>
            </div>
          )}
          <div className="grid grid-cols-3 gap-2 mb-3" data-testid="break-buttons">
            {QUICK_BREAK_TYPES.map(t => {
              const Icon = t.icon;
              const disabled = loading || !checkedIn;
              return (
                <button
                  key={t.id}
                  onClick={() => startBreak(t.id)}
                  disabled={disabled}
                  className={`${t.color} disabled:opacity-40 disabled:cursor-not-allowed text-white py-3 rounded-lg flex flex-col items-center justify-center space-y-0.5 transition-all`}
                  data-testid={`break-start-${t.id}`}
                >
                  <Icon size={20} />
                  <span className="text-sm font-medium">{t.label}</span>
                  {t.hint && <span className="text-[10px] opacity-80">{t.hint}</span>}
                </button>
              );
            })}
          </div>
          {errorMsg && (
            <div
              className="mb-3 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 flex items-start justify-between gap-2"
              data-testid="break-error"
            >
              <span className="flex-1">{errorMsg}</span>
              <button
                type="button"
                onClick={() => setErrorMsg('')}
                className="text-red-500 hover:text-red-700 font-bold"
                data-testid="break-error-close"
              >×</button>
            </div>
          )}
        </>
      )}      {ongoing && (
        <div className="space-y-2 mb-3">
          {overrun && (
            <div
              className="bg-red-50 border-2 border-red-500 px-3 py-3 rounded-lg text-sm text-red-900 animate-pulse"
              data-testid="break-overrun-banner"
              role="alert"
              aria-live="assertive"
            >
              <div className="flex items-start gap-2">
                <span className="text-2xl leading-none">🚨</span>
                <div className="flex-1">
                  <p className="font-bold mb-0.5">
                    Break overrun by {overrun.overByMin}m
                  </p>
                  <p className="text-[12px] leading-snug">
                    {OVERRUN_MESSAGE}
                  </p>
                </div>
              </div>
            </div>
          )}
          {endWarning && (
            <div
              className="bg-amber-50 border-2 border-amber-400 px-3 py-2.5 rounded-lg text-sm text-amber-900 flex items-center gap-2 animate-pulse"
              data-testid="break-ending-warning"
            >
              <span className="text-xl leading-none">⏰</span>
              <span className="flex-1">
                <strong>Only {endWarning.minsLeft}m</strong> to end {labelOf(endWarning.type)} — please tap{' '}
                <strong>End {labelOf(endWarning.type)}</strong> to avoid an overrun.
              </span>
            </div>
          )}
          <div className="bg-orange-50 border border-orange-200 p-3 rounded-lg text-sm">
            <p className="font-medium">{labelOf(ongoing.type)}</p>
            <p className="text-xs text-gray-600 mt-1">
              Started: {new Date(ongoing.out.time).toLocaleTimeString('en-IN')} • Allowed: {formatDuration(ongoing.allowed_duration_hours)}
            </p>
          </div>
          <button
            onClick={() => endBreak(ongoing.id)}
            disabled={loading}
            className="w-full bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white py-3 rounded-lg flex items-center justify-center space-x-2 font-semibold"
            data-testid="break-end-btn"
          >
            <StopCircle size={18} />
            <span>End {labelOf(ongoing.type)}</span>
          </button>
        </div>
      )}

      {todayRows.length > 0 && (
        <div className="border-t pt-3" data-testid="breaks-today-list">
          <p className="text-xs font-medium text-gray-500 mb-2 flex items-center space-x-1">
            <ClockIcon size={12} /><span>Today's Breaks ({todayRows.length})</span>
          </p>
          <div className="space-y-1">
            {todayRows.map(b => {
              const meta = BREAK_TYPES.find(t => t.id === b.type) || {};
              const Icon = meta.icon || Coffee;
              return (
                <div key={b.id} className="flex items-center justify-between text-xs py-1.5 px-2 rounded hover:bg-gray-50" data-testid={`break-row-${b.id}`}>
                  <div className="flex items-center space-x-2 min-w-0">
                    <Icon size={14} className="text-gray-500 flex-shrink-0" />
                    <span className="font-medium">{labelOf(b.type)}</span>
                    <span className="text-gray-500 truncate">
                      {b.out_time && new Date(b.out_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                      {b.in_time && ` → ${new Date(b.in_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}`}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2 flex-shrink-0">
                    {b.status === 'ongoing' ? (
                      <span className="text-orange-600 font-medium">ongoing</span>
                    ) : (
                      <>
                        <span className="font-semibold">{formatDuration(b.duration_hours)}</span>
                        {b.excess_duration_hours > 0 && (
                          <span className="text-red-600">+{formatDuration(b.excess_duration_hours)}</span>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {verifying && (
        <LocationVerifyingModal
          action={verifying.action}
          taken={verifying.taken}
          lastAccuracy={verifying.lastAccuracy}
        />
      )}

      {otpModal && (
        <GateOtpModal
          otp={otpModal.otp}
          label={otpModal.label}
          subtitle={otpModal.subtitle}
          autoApproved={otpModal.autoApproved}
          onClose={() => setOtpModal(null)}
        />
      )}
    </div>
  );
}
